<template>
    <div v-if="editor" :class="{'invalid' : (validation && validation.$error)}">
        <div class="tiptap-container notranslate" :class="containerClass">
            <slot name="menu" :editor="editor" :disabled="disabled" :toolbar-options="computedToolbarOptions">
                <MenuBar
                    :editor="editor"
                    :class="menuBarClass"
                    :toolbar-options="computedToolbarOptions"
                    :disabled="disabled">
                    <template #toolbar-start>
                        <slot name="toolbar-start"/>
                    </template>
                    <template #toolbar-end>
                        <slot name="toolbar-end"/>
                    </template>
                </MenuBar>
            </slot>
            <editor-content :editor="editor" class="text-content tiptap-editor-content"/>
        </div>
        <ValidationMessages v-if="!hideValidationMessage" :validation="validation"/>
    </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2';
import { Color } from '@tiptap/extension-color';
import ListItem from '@tiptap/extension-list-item';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import StarterKit from '@tiptap/starter-kit';
import { LegacyHighlight } from '@/common/components/tiptap/LegacyHighlightExtension';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';

import MenuBar from '@/common/components/tiptap/MenuBar.vue';
import ValidationMessages from '@/common/components/input/ValitationMessages.vue';
import {FontSize} from '@/common/components/tiptap/buttons/FontSizeExtension';
import {InlineImage} from '@/common/components/tiptap/extensions/InlineImage';
import {sanitizeHtml} from '@/filters';
import {TextEditorToolbarOptions} from '@/constants';

export default {
    name: 'TextEditor',
    props: {
        id: {
            type: String,
            default: 'text-editor'
        },
        value: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        },
        autofocus: {
            type: Boolean,
            default: false
        },
        toolbarOptions: {
            type: [String, Array]
        },
        validation: {
            type: Object,
            default: null
        },
        hideValidationMessage: {
            type: Boolean,
            default: false
        },
        containerClass: {
            type: [String, Object]
        },
        menuBarClass: {
            type: [String, Object],
            default: null
        },
        // Keeps <img> tags in the content instead of stripping them; there is no toolbar button for adding images
        allowImage: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            editor: null
        };
    },
    computed: {
        placeholderText() {
            return this.placeholder || this.$t('Write something …');
        },
        computedToolbarOptions() {
            if (Array.isArray(this.toolbarOptions)) {
                return this.toolbarOptions;
            }

            if (this.toolbarOptions === 'extended') {
                return [...TextEditorToolbarOptions.default, ...TextEditorToolbarOptions.extended];
            }

            return TextEditorToolbarOptions.default;
        }
    },
    watch: {
        value(value) {
            const html = this.normalizeHTML(value);
            const isSame = this.editor.getHTML() === html;

            if (isSame) {
                return;
            }

            this.editor.commands.setContent(html, false);
        },
        disabled(newVal) {
            if (this.editor) {
                this.editor.setOptions({ editable: !newVal });
            }
        },
        placeholderText() {
            if (this.editor) {
                this.editor.view.dispatch(this.editor.state.tr);
            }
        }
    },
    methods: {
        normalizeHTML(html) {
            if (!html) {
                return '';
            }
            // Convert old Quill editor line breaks<p><br></p> to <p></p>
            return html.replace(/<p>\s*<br\b[^>]*\/?>\s*<\/p>/gi, '<p></p>');
        },
        getEditorHTML() {
            let editorHTML = this.editor.getHTML();

            if (editorHTML === '<p></p>' || editorHTML === '<p><br></p>') {
                editorHTML = '';
            }

            return editorHTML;
        },

        // Adds HTML after the current content and emits it like a user edit. Returns the
        // content as the editor keeps it, without what its schema drops (e.g. images).
        appendContent(html) {
            const current = this.getEditorHTML();
            this.editor.commands.setContent(current + html, true);
            return this.getEditorHTML();
        },

        handleUpdate() {
            this.$emit('input', this.getEditorHTML());
        },

        handleBlur() {
            this.$emit('blur', this.getEditorHTML());
        }
    },
    components: {
        EditorContent,
        MenuBar,
        ValidationMessages
    },
    mounted() {
        this.editor = new Editor({
            content: this.normalizeHTML(this.value),
            autofocus: this.autofocus,
            editable: !this.disabled,
            extensions: [
                Color.configure({ types: [TextStyle.name, ListItem.name] }),
                TextStyle.configure({
                    mergeNestedSpanStyles: true,
                    types: [ListItem.name]
                }),
                StarterKit.configure({
                    blockquote: false,
                    code: false,
                    horizontalRule: false,
                    image: false,
                    table: false,
                    hr: false,
                    img: false,
                    iframe: false
                }),
                Underline,
                LegacyHighlight.configure({
                    multicolor: true
                }),
                Link.configure({
                    openOnClick: false,
                    autolink: true,
                    linkOnPaste: true,
                    defaultProtocol: 'https'
                }),
                Placeholder.configure({
                    placeholder: () => this.placeholderText
                }),
                TextAlign.configure({
                    types: ['paragraph'] // Enable for specific nodes
                }),
                FontSize,
                ...(this.allowImage ? [InlineImage.configure({inline: true, allowBase64: true})] : [])
            ],
            editorProps: {
                attributes: {
                    spellcheck: 'false' // Disable default browser spellchecking in Tiptap editor
                },
                transformPastedHTML(html) {
                    return sanitizeHtml(html);
                }
            },
            onUpdate: () => {
                this.handleUpdate();
            },
            onBlur: () => {
                this.handleBlur();
            }
        });
    },

    beforeDestroy() {
        this.editor.destroy();
    }
};
</script>

<style scoped lang="scss"> // TODO: needs to be scoped or extracted to a global stylesheet (unscoped styles break plans styling)
.popup-content {
    max-width: 308px !important;
}
</style>
